import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { Box, Tooltip } from '@mui/material';

interface SplitPaneMAIQProps {
  left: ReactNode;
  right: ReactNode;
  defaultWidth?: number; // percent
  minWidth?: number; // percent
  maxWidth?: number; // percent
}

const SplitPaneMAIQ: React.FC<SplitPaneMAIQProps> = ({
  left,
  right,
  defaultWidth = 32,
  minWidth = 20,
  maxWidth = 60,
}) => {
  const [chatWidth, setChatWidth] = useState(defaultWidth); // percent
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [isDraggingState, setIsDraggingState] = useState(false); // for overlay
  const resizerRef = useRef<HTMLDivElement>(null);

  // Mouse event handlers for resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    setIsDraggingState(true);
    document.body.style.cursor = 'col-resize';
  };
  const handleDoubleClick = () => {
    setChatWidth(defaultWidth);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setChatWidth(w => Math.max(minWidth, w - 2));
    } else if (e.key === 'ArrowRight') {
      setChatWidth(w => Math.min(maxWidth, w + 2));
    }
  };
  // Touch support
  const handleTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true;
    setIsDraggingState(true);
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      let percent = (x / rect.width) * 100;
      percent = Math.max(minWidth, Math.min(maxWidth, percent));
      setChatWidth(percent);
    };
    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        setIsDraggingState(false);
        document.body.style.cursor = '';
      }
    };
    // Touch events
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      let percent = (x / rect.width) * 100;
      percent = Math.max(minWidth, Math.min(maxWidth, percent));
      setChatWidth(percent);
    };
    const handleTouchEnd = () => {
      if (isDragging.current) {
        isDragging.current = false;
        setIsDraggingState(false);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [minWidth, maxWidth, defaultWidth]);

  return (
    <Box
      ref={containerRef}
      sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        gap: 2, 
        height: '100%', 
        overflow: 'hidden', 
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{ 
        flex: `1 1 ${chatWidth}%`, 
        minWidth: 0, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {left}
      </Box>
      <Tooltip title="Drag to resize. Double-click to reset." arrow placement="right">
        <Box
          ref={resizerRef}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          tabIndex={0}
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={chatWidth}
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          sx={{
            width: '16px',
            cursor: 'col-resize',
            background: 'transparent',
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            outline: 'none',
            transition: 'background 0.2s',
            '&:hover .resize-handle, &.dragging .resize-handle, &:focus .resize-handle': {
              backgroundColor: 'secondary.main',
              boxShadow: '0 0 0 4px rgba(44, 181, 67, 0.10)',
            },
          }}
          className={isDraggingState ? 'dragging' : ''}
        >
          <Box
            className="resize-handle"
            sx={{
              width: '6px',
              height: '48px',
              borderRadius: '8px',
              backgroundColor: 'divider',
              boxShadow: '0 1px 8px 0 rgba(15, 40, 76, 0.10)',
              transition: 'background 0.2s, box-shadow 0.2s',
              position: 'relative',
            }}
          />
          {/* Overlay on drag */}
          {isDraggingState && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                background: 'rgba(44,181,67,0.08)',
                pointerEvents: 'none',
                boxShadow: '0 0 0 8px rgba(44,181,67,0.10)',
                transition: 'background 0.2s, box-shadow 0.2s',
              }}
            />
          )}
        </Box>
      </Tooltip>
      <Box sx={{ 
        flex: `0 0 ${100 - chatWidth}%`, 
        minWidth: 0, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {right}
      </Box>
    </Box>
  );
};

export default SplitPaneMAIQ; 